import { apiClient } from '@/shared/api/api.client'
import type { ApprovalDTO, PageEnvelope } from '@/features/request-center'
import type { StatsAdminDTO, StatsMyDTO } from '@kfolks/types'

const APPROVALS_LIMIT = 10
const DASHBOARD_LIST_LIMIT = 10
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Generic count fetcher used by the cross-module Admin tiles. Accepts both
 * `T[]` and `{ data: T[]; total: number }` response shapes — every existing
 * list endpoint in the codebase returns one of those two.
 */
async function fetchCount(endpoint: string): Promise<number> {
	const res = await apiClient.get<unknown[] | { data: unknown[]; total: number }>(endpoint)
	const body = res.data
	if (body && typeof body === 'object' && !Array.isArray(body) && 'total' in (body as Record<string, unknown>)) {
		return (body as { total: number }).total
	}
	if (Array.isArray(body)) return body.length
	return 0
}

interface PagedRequestList {
	data: unknown[]
	total: number
}

interface RequestTypeCatalogEntry {
	id: string
	name: string
}

export const dashboardApi = {
	getStatsMy: async (): Promise<StatsMyDTO> => (await apiClient.get<StatsMyDTO>('/requests/stats/my')).data,

	getOpenRequests: async (): Promise<PagedRequestList> =>
		(
			await apiClient.get<PagedRequestList>(
				`/requests?filters[submitter]=me&filters[state]=!final&limit=${DASHBOARD_LIST_LIMIT}`,
			)
		).data,

	getRecentActivity: async (): Promise<{ data: unknown[] }> =>
		(await apiClient.get<{ data: unknown[] }>(`/requests/recent-activity?limit=${DASHBOARD_LIST_LIMIT}`)).data,

	getClosedLast30d: async (): Promise<PagedRequestList> => {
		const since = new Date(Date.now() - ONE_MONTH_MS).toISOString()
		const url = `/requests?filters[submitter]=me&filters[state]=completed&filters[from]=${encodeURIComponent(since)}&limit=1`
		return (await apiClient.get<PagedRequestList>(url)).data
	},

	getRequestTypesCatalog: async (): Promise<RequestTypeCatalogEntry[]> =>
		(await apiClient.get<RequestTypeCatalogEntry[]>('/requests/types?includeInactive=true')).data,

	getPendingApprovals: async (): Promise<PageEnvelope<ApprovalDTO>> =>
		(
			await apiClient.get<PageEnvelope<ApprovalDTO>>(
				`/requests/approvals?filters[assignedToMe]=true&limit=${APPROVALS_LIMIT}`,
			)
		).data,

	getAdminStats: async (): Promise<StatsAdminDTO> => (await apiClient.get<StatsAdminDTO>('/requests/stats')).data,

	getPendingIdmConfirmations: (): Promise<number> =>
		fetchCount('/employees/idm-requests?filters[status]=pending_confirmation'),

	getCount: fetchCount,
}
